import { SESSION_ENDED_PARAM, SESSION_ENDED_VALUE } from "@/lib/auth/session-ended";
import { BffErrorCode } from "@/lib/http/bff-error-codes";
import { BffFailure } from "@/lib/http/client-http";

/**
 * A panel write refused because the session no longer holds a usable token.
 *
 * Why this exists: the BFF answers `UNAUTHENTICATED` and the screen used to do nothing but show
 * a toast ("Sua sessão expirou. Entre novamente para salvar."), leaving the person exactly where
 * they were — with a session that will refuse the **next** write too. In the curation that closes
 * a trap: nothing can be saved, so the list keeps no piece; and a draft with no piece is sent
 * back to the curation by the panel's own guard (`(painel)/layout.tsx`). The person presses
 * Continuar, the address flickers to the panel and returns, forever.
 *
 * A dead session is not a save failure to be reported — it is a session to be ended. So the
 * answer here is to end it and come back: `/api/auth/logout?expired=1` deletes the cookie and
 * lands on onboarding with the mark that makes it always show the login (`session-ended.ts`),
 * carrying `next` so the person returns to the very screen they were on. The draft is on the
 * backend, untouched, and the curation reopens with it.
 */

export function isSessionExpired(error: unknown): boolean {
  return error instanceof BffFailure && error.code === BffErrorCode.UNAUTHENTICATED;
}

/**
 * Ends the session and sends the person to log in again, returning here afterwards.
 *
 * `location.assign`, not the router: the cookie has to be dropped by the server on a real
 * navigation, and a client transition would keep the dead session in place.
 */
export function endExpiredSession(): void {
  const here = `${window.location.pathname}${window.location.search}`;
  const params = new URLSearchParams({ expired: "1", next: here });

  window.location.assign(`/api/auth/logout?${params.toString()}`);
}

/**
 * The two together, for a write's failure path: true when the session was the problem and the
 * person is already on their way to the login, so the caller does not also show a toast that
 * the navigation would wipe.
 */
export function handledAsExpiredSession(error: unknown): boolean {
  if (!isSessionExpired(error)) return false;

  endExpiredSession();

  return true;
}

/** Where an ended session lands, exported for the test that proves the round trip. */
export const SESSION_ENDED_LANDING = `${SESSION_ENDED_PARAM}=${SESSION_ENDED_VALUE}`;
